import { useState } from 'react';

import { OnboardingState } from '../../../../../../types/OnboardingState';
import { DropdownMenu, DropdownMenuOption } from '../../../../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../../../../cdl/IconButton/IconButton';
import { Modal } from '../../../../../cdl/Modal/Modal';
import { useToasts } from '../../../../../cdl/Toast/useToasts';
import { CancelPopup } from '../../../../../common/CancelPopup/CancelPopup';
import { SupplierInfoPopup } from '../../../../../common/CoreDataPopup/SupplierInfoPopup';
import { DeleteConfirmPopup } from '../../../../../common/DeleteConfirmPopup/DeleteConfirmPopup';
import { translate } from '../../../../../common/helpers/translate.helper';
import { useRole } from '../../../../../common/hooks/useRole';
import { IconDots } from '../../../../../common/icons/cdl/Dots';
import { useFlags } from '../../../../../common/launchDarkly/useFlags';
import { Box } from '../../../../../common/ui/Box';
import { useOfferUpdate } from '../../../../../offer/detail/common/hooks/useOfferUpdate';
import { OfferState } from '../../../../../offer/model/OfferState';
import { usePriceAvailabilityCheck } from '../../../../../offer/OfferOverview/actions/usePriceAvailabilityCheck';
import { usePriceAvailabilityCreate } from '../../../../../offer/OfferOverview/actions/usePriceAvailabilityCreate';
import { SupplierOnboardingRequestModal } from '../../../../../supplierOnboarding/components/SupplierOnboardingRequestModal';
import { useCancelSupplierOnboarding } from '../../../../../supplierOnboarding/hooks/useCancelSupplierOnboarding';
import { useOfferCardContext } from '../hooks/useOfferCardContext';

import { ApprovalRequestButton } from './ApprovalRequestButton';
import { getCustomerOfferCancelPopupText } from './getCustomerOfferCancelPopupText';

export const ActionsRow = () => {
    const { offer, isLastOpenOffer, isSpotAvailable, onboarding } = useOfferCardContext();
    const [showCancelPopup, setShowCancelPopup] = useState(false);
    const [showSupplierDetailsModal, setShowSupplierDetailsModal] = useState(false);
    const [showOnboardingModal, setShowOnboardingModal] = useState(false);
    const [showCancelOnboardingPopup, setShowCancelOnboardingPopup] = useState(false);
    const flags = useFlags();
    const role = useRole();
    const { mutate: createPAI } = usePriceAvailabilityCreate();
    const { addToast, addErrorToast } = useToasts();
    const updateOfferMutation = useOfferUpdate();

    const { data: paiData } = usePriceAvailabilityCheck({
        offerId: offer.id,
    });

    const cancelOnboarding = useCancelSupplierOnboarding();
    const onboardingState = onboarding?.state;
    // The onboarding is shared across a supplier's offers but anchored to the one it was initiated on.
    const isOnboardingOffer = onboarding !== undefined && onboarding.offerId === offer.id;
    // A draft or active onboarding occupies the supplier's slot; a canceled one can be re-requested.
    const supplierOnboardingOpen = onboarding !== undefined && onboardingState !== OnboardingState.CANCELED;
    const canRequestOnboarding =
        !supplierOnboardingOpen || (isOnboardingOffer && onboardingState === OnboardingState.DRAFT);
    // The supplier already onboards through another of their offers — surface the action as disabled.
    const requestOnboardingDisabled = supplierOnboardingOpen && !isOnboardingOffer;
    const canCancelOnboarding =
        isOnboardingOffer &&
        (onboardingState === OnboardingState.REQUESTED ||
            onboardingState === OnboardingState.IN_PROGRESS ||
            onboardingState === OnboardingState.SUBMITTED);

    const handleRequestPAI = () => {
        createPAI({
            offerId: offer.id,
            customerId: offer.customerId,
            supplierId: offer.supplierId,
        });
    };

    const dropdownOptions: DropdownMenuOption[] = [];

    if (role.isCustomer()) {
        dropdownOptions.push({
            label: translate('order.detail.offerCard.actions.viewSupplierDetails'),
            onSelect: () => setShowSupplierDetailsModal(true),
        });
    }

    if (offer.isSpot() && role.isCustomer() && role.hasWriteRights(offer.customerId)) {
        if (canRequestOnboarding || requestOnboardingDisabled) {
            dropdownOptions.push({
                label: translate('order.detail.offerCard.actions.requestKyc'),
                onSelect: () => setShowOnboardingModal(true),
                disabled: requestOnboardingDisabled,
            });
        }
        if (canCancelOnboarding) {
            dropdownOptions.push({
                label: translate('order.detail.offerCard.actions.cancelKycRequest'),
                onSelect: () => setShowCancelOnboardingPopup(true),
                destructive: true,
            });
        }
    }

    if (role.isCustomer() && role.hasWriteRights(offer.customerId) && offer.isEnquiry()) {
        dropdownOptions.push({
            label: translate(
                offer.state === OfferState.ENQUIRY
                    ? 'order.detail.offerCard.actions.cancelRequest'
                    : 'order.detail.offerCard.actions.cancelOffer'
            ),
            onSelect: () => setShowCancelPopup(true),
            destructive: true,
        });
    }

    if (flags.priceAvailabilityRequest && flags.priceAvailabilityRequestSuppliers?.includes(offer.supplierId)) {
        dropdownOptions.push({
            label: translate('offer.priceAvailability.requestButton'),
            onSelect: handleRequestPAI,
            disabled: paiData?.hasPAI ?? false,
        });
    }

    const handleCancelSubmit = ({ cancelReason }: { cancelReason: string }) => {
        let nextState = OfferState.QUOTE_CANCELED;

        if (offer.state === OfferState.ENQUIRY) {
            nextState = OfferState.ENQUIRY_CANCELED;
        } else if (offer.isSpot()) {
            nextState = OfferState.QUOTE_DECLINED;
        }

        updateOfferMutation.mutate(
            {
                offer: {
                    ...offer,
                    state: nextState,
                    cancelReason,
                },
                offerId: offer.id,
            },
            {
                onSuccess: () => {
                    setShowCancelPopup(false);
                },
                onError: () => {
                    addErrorToast(translate('global.genericToastError'));
                },
            }
        );
    };

    return (
        <>
            <Box display="flex" gap="8px" width="100%">
                <Box flex="4">
                    <ApprovalRequestButton />
                </Box>
                {dropdownOptions.length ? (
                    <DropdownMenu
                        options={dropdownOptions}
                        align="end"
                        trigger={<IconButton Icon={IconDots} emphasis="medium" />}
                    />
                ) : null}
            </Box>

            {showCancelPopup ? (
                <CancelPopup
                    isOpen={showCancelPopup}
                    onSubmit={handleCancelSubmit}
                    onDismiss={() => setShowCancelPopup(false)}
                    isSubmitting={updateOfferMutation.isPending}
                    text={getCustomerOfferCancelPopupText(offer.state, isLastOpenOffer, isSpotAvailable)}
                />
            ) : null}

            {showSupplierDetailsModal ? (
                <Modal isOpen={showSupplierDetailsModal} onDismiss={() => setShowSupplierDetailsModal(false)}>
                    <SupplierInfoPopup supplier={offer.supplier} />
                </Modal>
            ) : null}

            {showOnboardingModal ? (
                <SupplierOnboardingRequestModal
                    offerId={offer.id}
                    customerId={offer.customerId}
                    onboarding={onboarding ?? null}
                    isOpen={showOnboardingModal}
                    onDismiss={() => setShowOnboardingModal(false)}
                />
            ) : null}

            {showCancelOnboardingPopup ? (
                <DeleteConfirmPopup
                    isOpen={showCancelOnboardingPopup}
                    headline={translate('supplierOnboarding.cancelConfirm.headline')}
                    text={translate('supplierOnboarding.cancelConfirm.text')}
                    deleteButtonText={translate('order.detail.offerCard.actions.cancelKycRequest')}
                    isSubmitting={cancelOnboarding.isPending}
                    onSubmit={() =>
                        cancelOnboarding.mutate(offer.id, {
                            onSuccess: () => {
                                addToast(translate('supplierOnboarding.cancelSuccess'));
                                setShowCancelOnboardingPopup(false);
                            },
                            onError: () => addErrorToast(translate('supplierOnboarding.cancelError')),
                        })
                    }
                    onDismiss={() => setShowCancelOnboardingPopup(false)}
                />
            ) : null}
        </>
    );
};
